/**
 * "Ask the user for a PDApp icon" — the contract between the worker tool and the
 * chat card (#788, Chu's second review).
 *
 * The agent asks with `pdapp_icon` action `ask`; the browser recognises the
 * question by its HEADER and answers it with a card of its own instead of the
 * generic prompt, the way the credential, A2UI and access cards already do. The
 * icon the user chose rides back in the reply's `_result` side-channel.
 *
 * Here, in `@papan/types`, because both ends have to agree and one of them is a
 * worker (no DOM, no React) while the other is the SPA.
 */
import type { PortablePdappMetadata } from './miniapp-icon-svg.js'

/** What makes a question THIS question. Model-writable text never decides: the
 *  header is stamped by the tool, and the card also checks the emitting tool. */
export const PDAPP_ICON_QUESTION_HEADER = 'PdappIcon'

/** The two answers — ONE spelling for both ends, since the tool reads anything
 *  but CHOOSE as a decline (a renamed label on one side only would turn every
 *  Confirm into a "not now"). The card sends CHOOSE on Confirm, the icon riding
 *  in `_result`, and DECLINE on Cancel. */
export const PDAPP_ICON_CHOOSE_ANSWER = 'Choose an icon'
export const PDAPP_ICON_DECLINE_ANSWER = 'Not now'

/** What the agent asked for. */
export interface PdappIconRequest {
  /** The agent's one line about what the icon is for, or null. Plain text. */
  note: string | null
}

/**
 * Recognise the request in a pending question, or answer null and let the
 * generic prompt render it.
 *
 * `emittingTool` is the name of the tool call that RAISED the question, stamped
 * server-side. Everything else here is model-writable — the ungated `question`
 * tool passes a header and an option description through verbatim — so this is
 * what stops a prompt-injected turn putting a Papan-looking card in front of
 * someone. It fails OPEN when the caller cannot resolve a name (a card recovered
 * after a reload), which costs nothing against the live attack: that call is
 * active, and therefore resolvable.
 *
 * The stakes are lower than the access card's — this card performs no mutation
 * and uses no session; the worst a forgery achieves is a sentence the model
 * wrote and an icon handed back to it — but the check is the same shape, and a
 * card that looks like Papan asking is worth not handing out.
 */
export function parsePdappIconRequest(
  question: { header?: string; options?: Array<{ label?: string; description?: string }> },
  emittingTool?: string,
): PdappIconRequest | null {
  if (question.header !== PDAPP_ICON_QUESTION_HEADER) return null
  if (emittingTool && emittingTool !== 'pdapp_icon') return null
  const raw = question.options?.[0]?.description
  if (typeof raw !== 'string') return null
  try {
    const parsed = JSON.parse(raw) as { tool?: unknown; action?: unknown; note?: unknown }
    if (parsed.tool !== 'pdapp_icon' || parsed.action !== 'ask') return null
    return { note: typeof parsed.note === 'string' && parsed.note.trim() ? parsed.note.trim() : null }
  } catch {
    return null
  }
}

/** The icon the user chose, on its way back to the worker. */
export interface ChosenIconReply {
  meta: PortablePdappMetadata
  /** Whether it should also OPEN the app it came from. */
  link: boolean
  /** Where it should land, relative to the element the author had selected.
   *  Absent when nothing was selected — there is then nothing to be relative to,
   *  and the model places it by its own judgement, as it always did. */
  placement?: Placement
}

/** The export's own field limits, restated where browser-supplied metadata is
 *  read, so both producers answer to the same bounds. */
const PORTABLE_NAME_MAX = 120
const PORTABLE_VERSION_MAX = 40
const PORTABLE_DESCRIPTION_MAX = 500

/** Biggest reply we will read at all. An icon with uploaded art carries it
 *  inlined (~130 KB at the export's ceiling, two slots); past that something is
 *  wrong rather than large. */
const MAX_REPLY_CHARS = 400_000

/**
 * Read the card's reply defensively.
 *
 * It comes from the user's own browser, so this is not a trust boundary — but it
 * crosses a process and a version skew, and the worker draws from whatever it
 * gets. Structure is checked here; VALUES are the renderer's to make safe
 * (`safeColor`, `xmlEscape`, `safeImageDataUri` all run at draw time), which is
 * the same division the SDK's own validator keeps.
 */
export function readChosenIcon(raw: unknown): ChosenIconReply | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as { meta?: unknown; link?: unknown; placement?: unknown }
  const meta = r.meta as PortablePdappMetadata | undefined
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null
  if (typeof meta.name !== 'string' || !meta.name.trim()) return null
  if (meta.icon !== undefined && meta.icon !== null && typeof meta.icon !== 'object') return null
  // `bg` is STRUCTURE, not a value, and this is the line where that distinction
  // earns its keep. `resolveIconRenderModel` dereferences `resolved.bg.type`
  // unguarded, so an icon of `{}` — an older SPA, a partial spec, a hand-rolled
  // reply — threw a TypeError inside `buildPdappIconMarkup`, which the worker
  // calls OUTSIDE its try. The whole tool invocation failed instead of
  // answering a sentence.
  //
  // Deliberately NOT `MiniappIconSpecSchema.safeParse`: that schema is `.strict()`
  // and a COPIED icon legitimately carries `assets`/`glyph` on top of it, so
  // parsing here would reject every image-backed copy. The renderer's actual
  // requirement is the one checked.
  if (meta.icon && typeof (meta.icon as { bg?: unknown }).bg !== 'object') return null
  if (meta.icon && (meta.icon as { bg?: unknown }).bg === null) return null
  for (const k of ['version', 'description', 'folderId', 'tenantId'] as const) {
    if (meta[k] !== undefined && typeof meta[k] !== 'string') return null
  }
  try {
    if (JSON.stringify(meta).length > MAX_REPLY_CHARS) return null
  } catch {
    return null // circular, or otherwise not something to draw
  }
  // The TEXT fields are printed back to the model. The export bounds them at the
  // source (120/40/500); metadata that came from a browser has only the total
  // size above, and a 300 KB `description` inside it would be a 300 KB tool
  // result. Same limits, applied where the two paths meet.
  return {
    meta: {
      ...meta,
      name: meta.name.slice(0, PORTABLE_NAME_MAX),
      ...(meta.version ? { version: meta.version.slice(0, PORTABLE_VERSION_MAX) } : {}),
      ...(meta.description ? { description: meta.description.slice(0, PORTABLE_DESCRIPTION_MAX) } : {}),
    },
    link: r.link === true,
    // Bounded to the four the card can offer. Anything else — a stale client, a
    // hand-rolled reply — is dropped rather than passed through into a sentence
    // the model is asked to act on.
    placement: PLACEMENTS.includes(r.placement as Placement) ? (r.placement as Placement) : undefined,
  }
}

/** Where an inserted icon goes, relative to the element the author selected.
 *
 *  FOUR, and element-relative on purpose. A page-relative pair ("top of the
 *  page" / "end of the page") was tried and taken back out: the web is made of
 *  containers, so the answer to "where" is always some element you can point at,
 *  and a second family of placements that means something different from the
 *  other four is a bigger idea than the choice is worth. Where there is no
 *  selection there is no choice — the insert goes to the end of the page, and
 *  the control is not shown. */
export type Placement = 'before' | 'after' | 'inside-start' | 'inside-end'
const PLACEMENTS: readonly Placement[] = ['before', 'after', 'inside-start', 'inside-end']

export const PLACEMENT_INSTRUCTION: Record<Placement, string> = {
  before: 'immediately BEFORE the element the user currently has selected',
  after: 'immediately AFTER the element the user currently has selected',
  'inside-start': 'INSIDE the element the user currently has selected, as its FIRST child',
  'inside-end': 'INSIDE the element the user currently has selected, as its LAST child',
}
